(() => {
  'use strict';

  const RUNTIME_KEY = 'SednaBookingSectionRuntime';
  const ROOT_SELECTOR = '[data-sedna-booking-section]';
  const BODY_CLASS = 'sedna-booking-theme-active';
  const WIDGET_SCRIPT = 'widget-booking.js';
  const WIDGET_STYLESHEET = 'widget-booking.css';
  const VENDOR_MODAL_SELECTOR = [
    '#sedna-photo-modal',
    '#sedna-validation-modal',
    '#sedna-reservation-result-modal',
  ].join(', ');
  const DEFAULT_NIGHTS = 7;
  const MAX_NIGHTS = 365;
  const LOAD_TIMEOUT = 30000;
  const BOOKING_PARAMS = Object.freeze([
    'id_boat',
    'id_tbf1',
    'dateStart',
    'datestart',
    'dateEnd',
    'dateend',
    'nights',
    'crew',
    'pax',
    'user_role',
    'query',
    'agent_id',
    'agent_name',
    'returnUrl',
  ]);
  const SEARCH_PARAMS = Object.freeze([
    'query',
    'datestart',
    'nights',
    'pax_min',
    'pax_max',
    'pax',
  ]);
  const BOOKING_ONLY_PARAMS = Object.freeze([
    'id_boat',
    'id_tbf1',
    'dateStart',
    'dateEnd',
    'dateend',
    'crew',
    'agent_id',
    'agent_name',
    'user_role',
    'returnUrl',
    'searchUrl',
  ]);

  if (window[RUNTIME_KEY]) {
    window[RUNTIME_KEY].initialize(document);
    return;
  }

  const instances = new Map();
  const assetPromises = new Map();
  let activeRoot = null;

  const collectRoots = (scope) => {
    const roots = [];

    if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
    if (scope?.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    return [...new Set(roots)];
  };

  const createUtcDate = (year, month, day) => {
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return null;
    }
    return date;
  };

  const parseDate = (value) => {
    const raw = String(value || '').trim();
    let match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) return createUtcDate(Number(match[1]), Number(match[2]), Number(match[3]));

    match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) return createUtcDate(Number(match[3]), Number(match[2]), Number(match[1]));

    return null;
  };

  const addDays = (date, days) => {
    const result = new Date(date.getTime());
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  };

  const differenceInDays = (start, end) =>
    Math.round((end.getTime() - start.getTime()) / 86400000);

  const formatWidgetDate = (date) => {
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${date.getUTCFullYear()}`;
  };

  const formatSearchDate = (date) => {
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    return `${date.getUTCFullYear()}-${month}-${day}`;
  };

  const serializeUrl = (url) => url.toString().replace(/%2F/gi, '/');

  const normalizeBookingUrl = (rawUrl) => {
    const url = new URL(rawUrl, window.location.href);
    const params = url.searchParams;
    const idBoat = String(params.get('id_boat') || '').trim();
    const startDate = parseDate(params.get('dateStart') || params.get('datestart'));

    if (!/^\d+$/.test(idBoat) || Number(idBoat) <= 0 || !startDate) {
      return {
        changed: false,
        endDate: null,
        idBoat,
        nights: null,
        startDate,
        url,
        valid: false,
      };
    }

    const parsedNights = Number.parseInt(params.get('nights') || '', 10);
    const inboundEndDate = parseDate(params.get('dateEnd') || params.get('dateend'));
    const hasValidNights =
      Number.isInteger(parsedNights) && parsedNights > 0 && parsedNights <= MAX_NIGHTS;
    const inboundDifference = inboundEndDate
      ? differenceInDays(startDate, inboundEndDate)
      : null;

    let nights = DEFAULT_NIGHTS;
    let endDate = addDays(startDate, DEFAULT_NIGHTS);
    let reconciliation = 'default';

    if (hasValidNights) {
      nights = parsedNights;
      endDate = addDays(startDate, nights);
      reconciliation = inboundEndDate && inboundDifference !== nights ? 'nights' : 'consistent';
    } else if (
      inboundEndDate &&
      Number.isInteger(inboundDifference) &&
      inboundDifference > 0 &&
      inboundDifference <= MAX_NIGHTS
    ) {
      nights = inboundDifference;
      endDate = inboundEndDate;
      reconciliation = 'dateEnd';
    }

    const before = serializeUrl(url);
    params.set('nights', String(nights));
    params.set('dateEnd', formatWidgetDate(endDate));
    params.delete('dateend');
    const after = serializeUrl(url);

    return {
      changed: before !== after,
      endDate,
      idBoat,
      nights,
      reconciliation,
      startDate,
      url,
      valid: true,
    };
  };

  const getSafeSameOriginUrl = (value) => {
    if (!value) return null;
    try {
      const url = new URL(String(value), window.location.href);
      if (!['http:', 'https:'].includes(url.protocol)) return null;
      if (url.origin !== window.location.origin) return null;
      return url;
    } catch (_) {
      return null;
    }
  };

  const getSavedSearchUrl = () => {
    try {
      return window.sessionStorage?.getItem('sedna_manual_last_search_url') || '';
    } catch (_) {
      return '';
    }
  };

  const buildBackToSearchUrl = (root, normalized) => {
    const currentUrl = normalized?.url || new URL(window.location.href);
    const returnUrl = currentUrl.searchParams.get('returnUrl');
    const configuredUrl = root.dataset.backToSearchUrl?.trim() || '/pages/charter-search';
    const target =
      getSafeSameOriginUrl(returnUrl) ||
      getSafeSameOriginUrl(getSavedSearchUrl()) ||
      getSafeSameOriginUrl(configuredUrl) ||
      new URL('/pages/charter-search', window.location.origin);

    BOOKING_ONLY_PARAMS.forEach((name) => target.searchParams.delete(name));

    SEARCH_PARAMS.forEach((name) => {
      let value = currentUrl.searchParams.get(name);
      if (name === 'datestart' && normalized?.startDate) {
        value = formatSearchDate(normalized.startDate);
      } else if (name === 'nights' && normalized?.nights) {
        value = String(normalized.nights);
      }
      if (value != null && value !== '') target.searchParams.set(name, value);
    });

    return serializeUrl(target);
  };

  const setBackLink = (root, normalized = null) => {
    const link = root.querySelector('[data-sedna-booking-back]');
    if (!link) return;
    link.href = buildBackToSearchUrl(root, normalized);
  };

  const setState = (root, status, title, message) => {
    const state = root.querySelector('[data-sedna-booking-state]');
    const host = root.querySelector('[data-sedna-booking-host]');
    const titleElement = root.querySelector('[data-sedna-booking-state-title]');
    const messageElement = root.querySelector('[data-sedna-booking-state-message]');
    const spinner = root.querySelector('[data-sedna-booking-spinner]');
    const ready = status === 'ready';

    root.dataset.sednaBookingStatus = status;
    root.setAttribute('aria-busy', status === 'loading' ? 'true' : 'false');

    if (titleElement && title) titleElement.textContent = title;
    if (messageElement && message) messageElement.textContent = message;
    if (state) {
      state.hidden = ready;
      state.setAttribute('role', status === 'error' ? 'alert' : 'status');
    }
    if (spinner) spinner.hidden = status !== 'loading';
    if (host) {
      host.hidden = !ready;
      host.setAttribute('aria-hidden', ready ? 'false' : 'true');
    }
  };

  const setConfiguredState = (root, status) => {
    const prefix = status === 'missing' ? 'missing' : status === 'error' ? 'error' : 'unavailable';
    setState(
      root,
      status === 'missing' ? 'empty' : 'error',
      root.dataset[`${prefix}Title`],
      root.dataset[`${prefix}Message`],
    );
  };

  const buildAssetUrl = (root, filename) => {
    const baseUrl = root.dataset.baseUrl?.trim();
    const assetsPath = root.dataset.assetsPath?.trim();
    if (!baseUrl || !assetsPath) throw new Error('Sedna asset configuration is incomplete.');

    const base = new URL(baseUrl, window.location.href);
    if (!['http:', 'https:'].includes(base.protocol)) {
      throw new Error('Sedna base URL must use HTTP or HTTPS.');
    }

    const normalizedPath = `${assetsPath.replace(/^\/+|\/+$/g, '')}/${filename}`;
    const assetUrl = new URL(normalizedPath, `${base.origin}/`);
    const version = root.dataset.assetVersion?.trim();
    if (version) assetUrl.searchParams.set('v', version);
    return assetUrl.toString();
  };

  const loadStylesheet = (href, id) => {
    if (!href) return Promise.resolve();
    const key = `style:${id}:${href}`;
    if (assetPromises.has(key)) return assetPromises.get(key);

    const promise = new Promise((resolve, reject) => {
      const existing = document.getElementById(id);
      if (existing) {
        if (existing.dataset?.sednaLoaded === 'true' || existing.sheet) {
          resolve(existing);
          return;
        }
        existing.addEventListener('load', () => resolve(existing), { once: true });
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${href}`)), {
          once: true,
        });
        return;
      }

      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = href;
      link.addEventListener(
        'load',
        () => {
          link.dataset.sednaLoaded = 'true';
          resolve(link);
        },
        { once: true },
      );
      link.addEventListener('error', () => reject(new Error(`Failed to load ${href}`)), {
        once: true,
      });
      document.head.appendChild(link);
    });

    assetPromises.set(key, promise);
    return promise;
  };

  const loadScript = (src, id) => {
    if (window.SednaBookingWidget?.init) return Promise.resolve(window.SednaBookingWidget);
    const key = `script:${id}:${src}`;
    if (assetPromises.has(key)) return assetPromises.get(key);

    const promise = new Promise((resolve, reject) => {
      const confirmWidget = () => {
        if (window.SednaBookingWidget?.init) resolve(window.SednaBookingWidget);
        else reject(new Error('Sedna booking API is unavailable after the script loaded.'));
      };
      const existing = document.getElementById(id);
      if (existing) {
        existing.addEventListener('load', confirmWidget, { once: true });
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
          once: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;
      script.addEventListener('load', confirmWidget, { once: true });
      script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
        once: true,
      });
      document.head.appendChild(script);
    });

    assetPromises.set(key, promise);
    return promise;
  };

  const loadOptionalStylesheet = async (href, id) => {
    try {
      return await loadStylesheet(href, id);
    } catch (error) {
      console.warn(`[Sedna Booking] Optional stylesheet could not be loaded: ${href}`, error);
      return null;
    }
  };

  const withTimeout = (promise, milliseconds) =>
    new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(
        () => reject(new Error('Sedna booking details timed out.')),
        milliseconds,
      );
      Promise.resolve(promise).then(
        (value) => {
          window.clearTimeout(timeoutId);
          resolve(value);
        },
        (error) => {
          window.clearTimeout(timeoutId);
          reject(error);
        },
      );
    });

  const themeVendorModals = (root) => {
    const colorSchemeClass = root.dataset.colorSchemeClass?.trim();
    document
      .querySelectorAll(VENDOR_MODAL_SELECTOR)
      .forEach((modal) => {
        modal.classList.add('sedna-booking-modal--hyper');
        [...modal.classList]
          .filter((className) => className.startsWith('color-'))
          .forEach((className) => modal.classList.remove(className));
        if (colorSchemeClass) modal.classList.add(colorSchemeClass);
      });
  };

  const enhanceGalleryKeyboard = (item) => {
    if (item.dataset.sednaKeyboardReady === 'true') return;
    item.dataset.sednaKeyboardReady = 'true';
    item.tabIndex = 0;
    item.setAttribute('role', 'button');
    const imageAlt = item.querySelector('img')?.alt;
    item.setAttribute('aria-label', imageAlt ? `Open ${imageAlt}` : 'Open boat photo');
    item.addEventListener('keydown', (event) => {
      if (!['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      item.click();
    });
  };

  const enhanceVendorDom = (root) => {
    const host = root.querySelector('[data-sedna-booking-host]');
    if (!host) return;

    host.querySelectorAll('.conch-gallery-item[data-gallery-index]').forEach(enhanceGalleryKeyboard);
    host.querySelectorAll('button:not([type])').forEach((button) => {
      button.type = 'button';
    });
    host.querySelectorAll('#client-first-name, #client-last-name, #client-email, #client-phone').forEach(
      (input) => {
        input.required = true;
        input.setAttribute('aria-required', 'true');
      },
    );

    const passengers = host.querySelector('#nb_pax');
    const passengerLabel = passengers?.closest?.('.sedna-form-group')?.querySelector?.('label');
    if (passengers && passengerLabel) passengerLabel.htmlFor = passengers.id;

    const currency = host.querySelector('#booking_currency');
    const currencyLabel = currency?.closest?.('.sedna-form-group')?.querySelector?.('label');
    if (currency && currencyLabel) currencyLabel.htmlFor = currency.id;

    const aboutHeading = host.querySelector('.conch-details-pillar .conch-h1');
    if (aboutHeading) {
      aboutHeading.setAttribute('role', 'heading');
      aboutHeading.setAttribute('aria-level', '2');
    }

    const sidebar = host.querySelector('.conch-sticky-sidebar');
    if (sidebar) {
      sidebar.setAttribute('role', 'complementary');
      sidebar.setAttribute('aria-label', 'Booking price summary');
    }

    themeVendorModals(root);
  };

  const syncVendorState = (root) => {
    const host = root.querySelector('[data-sedna-booking-host]');
    if (!host) return 'error';

    if (host.querySelector('.conch-main-layout') && host.querySelector('.conch-widget-card')) {
      enhanceVendorDom(root);
      setState(root, 'ready');
      return 'ready';
    }

    if (host.querySelector('.sedna-error')) {
      setConfiguredState(root, 'error');
      return 'error';
    }

    return 'loading';
  };

  const initializeRoot = async (root) => {
    if (!root || instances.has(root)) return;

    const currentInstance = activeRoot ? instances.get(activeRoot) : null;
    if (activeRoot?.isConnected && currentInstance) {
      setConfiguredState(root, 'unavailable');
      return;
    }

    const host = root.querySelector('[data-sedna-booking-host]');
    if (!host) return;

    const instance = {
      observer: new MutationObserver(() => syncVendorState(root)),
    };
    instances.set(root, instance);
    activeRoot = root;
    document.body?.classList?.add(BODY_CLASS);
    instance.observer.observe(host, { childList: true, subtree: true });

    let normalized;
    try {
      normalized = normalizeBookingUrl(window.location.href);
    } catch (error) {
      console.warn('[Sedna Booking] Unable to read booking URL.', error);
      setBackLink(root);
      setConfiguredState(root, 'missing');
      return;
    }

    setBackLink(root, normalized);

    if (root.dataset.widgetEnabled !== 'true') {
      setConfiguredState(root, 'unavailable');
      return;
    }

    if (!normalized.valid) {
      setConfiguredState(root, 'missing');
      return;
    }

    if (normalized.changed) {
      window.history.replaceState(
        window.history.state,
        '',
        serializeUrl(normalized.url),
      );
    }
    root.dataset.sednaDateReconciliation = normalized.reconciliation;

    setState(
      root,
      'loading',
      root.dataset.loadingTitle,
      root.dataset.loadingMessage,
    );

    try {
      const widgetStylesheet = buildAssetUrl(root, WIDGET_STYLESHEET);
      const widgetScript = buildAssetUrl(root, WIDGET_SCRIPT);
      const iconStylesheet = root.dataset.iconStylesheetUrl?.trim();

      if (iconStylesheet) {
        void loadOptionalStylesheet(iconStylesheet, 'sedna-font-awesome');
      }

      await withTimeout(
        Promise.all([
          loadStylesheet(widgetStylesheet, 'sedna-booking-widget-css'),
          loadScript(widgetScript, 'sedna-booking-widget-js'),
        ]),
        LOAD_TIMEOUT,
      );

      if (!root.isConnected) return;
      await withTimeout(window.SednaBookingWidget.init(host.id), LOAD_TIMEOUT);
      if (!root.isConnected) return;
      if (syncVendorState(root) === 'loading') {
        throw new Error('Sedna booking widget did not render its detail layout.');
      }
    } catch (error) {
      console.error('[Sedna Booking] Widget initialization failed.', error);
      if (root.isConnected && syncVendorState(root) !== 'ready') {
        setConfiguredState(root, 'error');
      }
    }
  };

  const initialize = (scope = document) => {
    collectRoots(scope).forEach((root) => initializeRoot(root));
  };

  const cleanupVendorUi = () => {
    const photoModal = document.getElementById?.('sedna-photo-modal');
    if (photoModal?.classList?.contains('is-open')) {
      document.getElementById?.('sedna-photo-close')?.click?.();
    }

    document
      .querySelectorAll(VENDOR_MODAL_SELECTOR)
      .forEach((modal) => modal.classList.remove('is-open'));
    document.body?.classList?.remove('sedna-no-scroll');
  };

  const destroy = (scope) => {
    collectRoots(scope).forEach((root) => {
      const instance = instances.get(root);
      const wasActive = activeRoot === root;
      instance?.observer?.disconnect();
      instances.delete(root);
      if (wasActive) {
        cleanupVendorUi();
        activeRoot = null;
      }
    });

    if (![...instances.keys()].some((root) => root.isConnected)) {
      document.body?.classList?.remove(BODY_CLASS);
    }
  };

  window[RUNTIME_KEY] = Object.freeze({
    BOOKING_PARAMS,
    SEARCH_PARAMS,
    buildBackToSearchUrl,
    destroy,
    formatSearchDate,
    formatWidgetDate,
    initialize,
    normalizeBookingUrl,
    parseDate,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
